import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Collapse, Divider, IconButton, Tab, Tabs, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Activity, type FC, useCallback, useMemo, useState } from "react";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { ALPHA, INK_BLACK } from "../../theme/tokens";
import { LocationCensusTab } from "./LocationCensusTab";

type EditLocationCensusProps = {
	unitCensus: Record<string, number>;
	floorCensus: Record<string, number>;
	combinedCensus: Record<string, number>;
	censusValue: number;
	onUnitCensusChange: (unitCensus: Record<string, number>) => void;
	onFloorCensusChange: (floorCensus: Record<string, number>) => void;
	onCombinedCensusChange: (combinedCensus: Record<string, number>) => void;
};

export const EditLocationCensus: FC<EditLocationCensusProps> = ({
	unitCensus,
	floorCensus,
	combinedCensus,
	censusValue,
	onUnitCensusChange,
	onFloorCensusChange,
	onCombinedCensusChange,
}) => {
	const { hospitalData, chosenDepartment } = useHospitalData();
	const [activeTab, setActiveTab] = useState<number>(0);
	const [isExpanded, setIsExpanded] = useState<boolean>(false);

	// Convert combined census keys to display labels for UI
	// Backend stores as "unit|floor", UI displays as "Unit: X, Floor: Y"
	const combinedCensusForDisplay = useMemo(() => {
		const displayFormat: Record<string, number> = {};
		Object.entries(combinedCensus).forEach(([key, value]) => {
			const [unit, floor] = key.split("|");
			if (unit && floor) {
				displayFormat[`Unit: ${unit}, Floor: ${floor}`] = value;
			}
		});
		return displayFormat;
	}, [combinedCensus]);

	// Get all unique unit+floor combinations from department locations
	const combinedLocations = useMemo(() => {
		const combinations = new Set<string>();
		chosenDepartment.locations.forEach((loc) => {
			if (loc.unit && loc.floor) {
				const displayLabel = `Unit: ${loc.unit}, Floor: ${loc.floor}`;
				combinations.add(displayLabel);
			}
		});
		return Array.from(combinations).sort();
	}, [chosenDepartment.locations]);

	// Handle combined census changes - convert from display format back to backend format
	const handleCombinedCensusChange = useCallback(
		(newDisplayCensus: Record<string, number>) => {
			const backendFormat: Record<string, number> = {};
			Object.entries(newDisplayCensus).forEach(([label, value]) => {
				const match = label.match(/Unit: (.*?), Floor: (.*)/);
				if (match) {
					const [, unit, floor] = match;
					backendFormat[`${unit}|${floor}`] = value;
				}
			});
			onCombinedCensusChange(backendFormat);
		},
		[onCombinedCensusChange],
	);

	const totalUnitCensus = useMemo<number>(() => Object.values(unitCensus).reduce((sum, value) => sum + (value || 0), 0), [unitCensus]);
	const totalFloorCensus = useMemo<number>(() => Object.values(floorCensus).reduce((sum, value) => sum + (value || 0), 0), [floorCensus]);
	const totalCombinedCensus = useMemo<number>(
		() => Object.values(combinedCensusForDisplay).reduce((sum, value) => sum + (value || 0), 0),
		[combinedCensusForDisplay],
	);

	const unassignedUnitCensus = useMemo<number>(() => Math.max(0, censusValue - totalUnitCensus), [censusValue, totalUnitCensus]);
	const unassignedFloorCensus = useMemo<number>(() => Math.max(0, censusValue - totalFloorCensus), [censusValue, totalFloorCensus]);
	const unassignedCombinedCensus = useMemo<number>(() => Math.max(0, censusValue - totalCombinedCensus), [censusValue, totalCombinedCensus]);

	return (
		<>
			<Divider sx={{ width: "100%", mt: 1 }} />
			<Box sx={{ width: "100%" }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						cursor: "pointer",
						py: 1,
						"&:hover": { bgcolor: alpha(INK_BLACK, ALPHA.HOVER_OVERLAY) },
					}}
					onClick={() => setIsExpanded(!isExpanded)}
				>
					<Typography variant="subtitle2" sx={{ fontWeight: 600, color: "primary.main" }}>
						Census By Location
					</Typography>
					<IconButton size="small">{isExpanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
				</Box>
				<Collapse in={isExpanded}>
					<Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 2, minHeight: "36px" }}>
						<Tab label="Unit" sx={{ minHeight: "36px", py: 0.5 }} />
						<Tab label="Floor" sx={{ minHeight: "36px", py: 0.5 }} />
						<Tab label="Both" sx={{ minHeight: "36px", py: 0.5 }} />
					</Tabs>
					<Activity mode={activeTab === 0 ? "visible" : "hidden"}>
						<LocationCensusTab
							locations={hospitalData.units || []}
							census={unitCensus}
							onCensusChange={onUnitCensusChange}
							totalCensus={totalUnitCensus}
							unassignedCensus={unassignedUnitCensus}
							censusValue={censusValue}
						/>
					</Activity>
					<Activity mode={activeTab === 1 ? "visible" : "hidden"}>
						<LocationCensusTab
							locations={hospitalData.floors || []}
							census={floorCensus}
							onCensusChange={onFloorCensusChange}
							totalCensus={totalFloorCensus}
							unassignedCensus={unassignedFloorCensus}
							censusValue={censusValue}
						/>
					</Activity>
					<Activity mode={activeTab === 2 ? "visible" : "hidden"}>
						<LocationCensusTab
							locations={combinedLocations}
							census={combinedCensusForDisplay}
							onCensusChange={handleCombinedCensusChange}
							totalCensus={totalCombinedCensus}
							unassignedCensus={unassignedCombinedCensus}
							censusValue={censusValue}
						/>
					</Activity>
				</Collapse>
			</Box>
		</>
	);
};
